import { useEffect, useState } from "react";
import ListAdminMessages from "./ListAdminMessages";
import { getMessagesListForAdmin } from "../../apis";
import { MESSAGE_TYPES_ENUM } from "../../constants/globals";
import { Link, useLocation, useNavigate } from "react-router-dom";

const SidebarMessages = ({
  setCurrentMessageId,
  currentMessageId,
  sidebarMessagesToggle,
  setSidebarMessagesToggle,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [messagesListData, setMessagesListData] = useState([]);
  const [messagesPagination, setMessagesPagination] = useState({});
  const [currentMessageType, setCurrentMessageType] = useState(""); // Default to 'all'
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(
    new URLSearchParams(location.search).get("search") || ""
  );

  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    navigate({ search: params.toString() });
  }, [searchTerm, navigate]);

  const fetchMessages = async (type, searchTerm, page = 1, append = false) => {
    if (
      messagesPagination.current_page >= messagesPagination.last_page &&
      append
    )
      return;

    setIsLoading(true);
    try {
      const response = await getMessagesListForAdmin(type, searchTerm, page);

      const newMessages = response.data.data;
      const currentPage = response.data.current_page;
      const lastPage = response.data.last_page;

      setMessagesListData((prev) => {
        if (append && page !== 1) {
          const combined = [...prev, ...newMessages];

          const unique = Array.from(
            new Map(combined.map((msg) => [msg.id, msg])).values()
          );
          return unique;
        } else {
          return newMessages;
        }
      });

      setMessagesPagination({
        current_page: currentPage,
        last_page: lastPage,
      });
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreMessages = () => {
    if (messagesPagination.current_page < messagesPagination.last_page) {
      const nextPage = messagesPagination.current_page + 1;
      fetchMessages(currentMessageType, searchTerm, nextPage, true); // Append=true
    }
  };

  useEffect(() => {
    fetchMessages(currentMessageType, searchTerm, 1); // Always start at page 1
  }, [currentMessageType, searchTerm]);

  const handleTabClick = (type) => {
    setCurrentMessageType(type);
  };

  const handleFilter = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div
      className={`tw-w-1/3 tw-bg-white tw-border tw-rounded-xl tw-overflow-hidden max-xl:tw-absolute max-xl:tw-z-50 tw-transition-all tw-duration-100 ${
        sidebarMessagesToggle
          ? "max-xl:-tw-left-[0px]"
          : "max-xl:-tw-left-[400px]"
      } max-xl:tw-z-40 max-xl:tw-w-[350px]`}
    >
      {/* Sidebar Header */}
      <header className="tw-p-2 tw-pb-4 tw-flex tw-flex-col tw-gap-2 tw-bg-white tw-text-white">
        <Link
          to={"/partners-list-to-send-messages"}
          className="tw-flex tw-items-center tw-justify-center hover:!tw-bg-primaryLight tw-cursor-pointer !tw-no-underline !tw-bg-primary tw-p-2 !tw-rounded-full tw-font-bold"
        >
          <span className="material-icons">add</span>
          <span>Send New Message</span>
        </Link>

        <div className="w-1/2">
          <div className="tw-relative tw-text-gray-600">
            <input
              type="search"
              name="search"
              onChange={handleFilter}
              placeholder="Search"
              className="tw-bg-[#eee] tw-h-10 tw-px-5 tw-pr-10 tw-rounded-full tw-text-sm tw-focus:outline-none"
            />
            <div className="tw-absolute tw-right-4 tw-top-3">
              <svg
                className="tw-h-4 tw-w-4 tw-fill-current"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 56.966 56.966"
              >
                <path d="M55.146,51.887L41.588,37.786c3.486-4.144,5.396-9.358,5.396-14.786c0-12.682-10.318-23-23-23s-23,10.318-23,23  s10.318,23,23,23c4.761,0,9.298-1.436,13.177-4.162l13.661,14.208c0.571,0.593,1.339,0.92,2.162,0.92  c0.779,0,1.518-0.297,2.079-0.837C56.255,54.982,56.293,53.08,55.146,51.887z M23.984,6c9.374,0,17,7.626,17,17s-7.626,17-17,17  s-17-7.626-17-17S14.61,6,23.984,6z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="tw-flex tw-justify-center tw-gap-1 tw-bg-[#eee] tw-rounded-full tw-p-1">
          <div
            className={`tw-p-1 tw-px-3 tw-rounded-full tw-cursor-pointer ${
              currentMessageType === "" ? "tw-bg-primary" : "tw-text-black"
            }`}
            onClick={() => handleTabClick("")}
          >
            All
          </div>
          <div
            className={`tw-p-1 tw-px-3 tw-rounded-full tw-cursor-pointer ${
              currentMessageType === "unseen"
                ? "tw-bg-primary"
                : "tw-text-black"
            }`}
            onClick={() => handleTabClick("unseen")}
          >
            Unseen
          </div>
          <div
            className={`tw-p-1 tw-px-3 tw-rounded-full tw-cursor-pointer ${
              currentMessageType === MESSAGE_TYPES_ENUM.Message
                ? "tw-bg-primary"
                : "tw-text-black"
            }`}
            onClick={() => handleTabClick(MESSAGE_TYPES_ENUM.Message)}
          >
            Messages
          </div>
          <div
            className={`tw-p-1 tw-px-3 tw-rounded-full tw-cursor-pointer ${
              currentMessageType === MESSAGE_TYPES_ENUM.Complaint
                ? "tw-bg-primary"
                : "tw-text-black"
            }`}
            onClick={() => handleTabClick(MESSAGE_TYPES_ENUM.Complaint)}
          >
            Complaints
          </div>
        </div>
      </header>
      <div className="">
        <h4 className="tw-pb-2 !tw-m-0 !tw-text-gray-400 tw-font-bold !tw-ms-2">
          Messages
        </h4>

        <div id="scrollableSidebar" className="tw-overflow-y-auto tw-h-[60vh]">
          <ListAdminMessages
            isLoading={isLoading}
            messagesData={messagesListData}
            setCurrentMessageId={setCurrentMessageId}
            currentMessageId={currentMessageId}
            messagesPagination={messagesPagination}
            fetchMoreMessages={loadMoreMessages}
            setSidebarMessagesToggle={setSidebarMessagesToggle}
          />
        </div>
      </div>
    </div>
  );
};

export default SidebarMessages;
